import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api, { apiError } from "@/lib/api";
import { isValidPhone } from "@/lib/format";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Sparkles, CalendarIcon, Clock, ArrowRight } from "lucide-react";
import PublicKnownClient from "@/components/PublicKnownClient";
import { initMetaPixel, trackPixel } from "@/lib/metaPixel";

const WA_KEY = "clinit_public_wa";
const EMPTY = { name: "", whatsapp: "", address: "", reference: "", commune_id: "", quartier: "", collect_date: "", collect_time: "" };

const TIME_SLOTS = [];
for (let h = 8; h <= 18; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 18) TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}

export default function PublicOrder() {
  const [communes, setCommunes] = useState([]);
  const [pickDate, setPickDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [cfg, setCfg] = useState({ days: [1, 2, 3, 4, 5, 6], slots: [] });
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [step, setStep] = useState("phone");
  const [known, setKnown] = useState(null);
  const [useSaved, setUseSaved] = useState(true);
  const [checking, setChecking] = useState(false);

  const lookup = async (wa) => {
    setError("");
    setChecking(true);
    try {
      const { data } = await api.post("/public/lookup", { whatsapp: wa });
      if (data.known) { setKnown(data); setUseSaved(true); setStep("known"); } else { setKnown(null); setStep("new"); }
    } catch (err) {
      if (err.response?.status === 429) { setStep("new"); }
      setError(apiError(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    initMetaPixel();
    api.get("/public/communes").then((r) => setCommunes(r.data)).catch(() => {});
    api.get("/public/collection-config").then((r) => setCfg(r.data)).catch(() => {});
    const saved = localStorage.getItem(WA_KEY);
    if (saved && isValidPhone(saved)) {
      setForm((f) => ({ ...f, whatsapp: saved }));
      lookup(saved);
    }
  }, []);

  const notMe = () => {
    localStorage.removeItem(WA_KEY);
    setKnown(null);
    setStep("phone");
    setForm({ ...EMPTY });
  };
  const resetAll = () => {
    setDone(null); setPickDate(null); setError("");
    const wa = form.whatsapp;
    setForm({ ...EMPTY, whatsapp: wa });
    lookup(wa);
  };
  const savedMode = step === "known" && useSaved;
  const showAddress = step === "new" || (step === "known" && !useSaved);

  const slots = cfg.slots && cfg.slots.length ? cfg.slots : TIME_SLOTS;
  const allowedDays = cfg.days || [];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = savedMode
        ? { whatsapp: form.whatsapp, use_saved: true, collect_date: form.collect_date, collect_time: form.collect_time }
        : { ...form, use_saved: false };
      const { data } = await api.post("/public/orders", payload);
      localStorage.setItem(WA_KEY, form.whatsapp);
      trackPixel("Lead", { content_name: "Commande CLINIT", client_type: step === "known" ? "habitue" : "nouveau" }, data.order_number);
      setDone(data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-sm border p-8 max-w-md text-center animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <h1 className="text-2xl font-bold font-heading">Commande enregistrée !</h1>
          <p className="text-slate-500 mt-2">Votre commande a bien été enregistrée. Nous vous contacterons pour la collecte.</p>
          <div className="mt-5 bg-brand-50 border border-brand-200 rounded-2xl py-4">
            <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">Numéro de commande</div>
            <div data-testid="public-order-number" className="text-3xl font-extrabold font-mono text-brand-700 mt-1">
              {done.order_number}
            </div>
          </div>
          <Button
            data-testid="public-new-order-btn"
            onClick={resetAll}
            variant="outline"
            className="mt-6"
          >
            Passer une autre commande
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="brand-gradient text-white">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <Logo size={44} />
          <div className="flex items-center gap-2 mt-6 text-brand-yellow text-sm font-medium">
            <Sparkles className="w-4 h-4" /> Collecte & livraison à domicile
          </div>
          <h1 className="text-3xl font-extrabold font-heading mt-2">Passez votre commande en ligne</h1>
          <p className="text-brand-50 mt-2">Remplissez le formulaire, nous venons collecter votre linge.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 -mt-4">
        <form onSubmit={submit} className="bg-white rounded-3xl shadow-sm border p-6 space-y-5 animate-fade-in">
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Votre numéro WhatsApp</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <Input data-testid="public-whatsapp" value={form.whatsapp} disabled={step !== "phone"} onChange={set("whatsapp")} required placeholder="+243XXXXXXXXX"
                  onKeyDown={(e) => { if (e.key === "Enter" && step === "phone") { e.preventDefault(); if (isValidPhone(form.whatsapp)) lookup(form.whatsapp); } }}
                  className={`h-12 text-base ${form.whatsapp && !isValidPhone(form.whatsapp) ? "border-red-400 ring-1 ring-red-300" : ""}`} />
                <p className={`text-xs mt-1 ${form.whatsapp && !isValidPhone(form.whatsapp) ? "text-red-600" : "text-slate-400"}`}>Format : +243 suivi de 9 chiffres (ex. +243812345678)</p>
              </div>
              {step === "phone" ? (
                <Button type="button" data-testid="public-continue" onClick={() => { trackPixel("InitiateCheckout"); lookup(form.whatsapp); }} disabled={checking || !isValidPhone(form.whatsapp)} className="h-12 bg-brand-600 hover:bg-brand-700">
                  {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continuer <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                </Button>
              ) : (
                <Button type="button" variant="outline" data-testid="public-change-number" onClick={notMe} className="h-12 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Changer de numéro</Button>
              )}
            </div>
          </div>

          {step === "known" && known && <PublicKnownClient info={known} useSaved={useSaved} onChoose={setUseSaved} onNotMe={notMe} />}
          {step === "new" && <p data-testid="public-new-client" className="text-sm text-brand-700 bg-brand-50 rounded-xl px-4 py-3">Bienvenue ! Complétez vos informations.</p>}

          {step !== "phone" && (<>
          {showAddress && (<>
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">{step === "new" ? "Vos informations" : "Nouvelle adresse"}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {step === "new" && (
              <div className="sm:col-span-2">
                <Label>Nom complet *</Label>
                <Input data-testid="public-name" value={form.name} onChange={set("name")} required className="mt-1.5" />
              </div>
              )}
              <div className="sm:col-span-2">
                <Label>Adresse complète *</Label>
                <Input data-testid="public-address" value={form.address} onChange={set("address")} required className="mt-1.5" />
              </div>
              <div>
                <Label>Référence *</Label>
                <Input data-testid="public-reference" value={form.reference} onChange={set("reference")} required className="mt-1.5" />
              </div>
              <div>
                <Label>Quartier</Label>
                <Input data-testid="public-quartier" value={form.quartier} onChange={set("quartier")} className="mt-1.5" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Localisation</h3>
            <Label>Commune *</Label>
            <Select value={form.commune_id} onValueChange={set("commune_id")}>
              <SelectTrigger data-testid="public-commune" className="mt-1.5">
                <SelectValue placeholder="Sélectionnez votre commune" />
              </SelectTrigger>
              <SelectContent>
                {communes.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`public-commune-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </>)}

          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Collecte souhaitée</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="public-collect-date"
                      className={`mt-1.5 w-full justify-start font-normal ${!pickDate ? "text-slate-400" : ""}`}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-brand-600" />
                      {pickDate ? format(pickDate, "dd/MM/yyyy", { locale: fr }) : "jj/mm/aaaa"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={fr}
                      selected={pickDate}
                      disabled={[
                        { before: new Date(new Date().setHours(0, 0, 0, 0)) },
                        (d) => allowedDays.length > 0 && !allowedDays.includes(d.getDay()),
                      ]}
                      onSelect={(d) => {
                        setPickDate(d || null);
                        setForm((f) => ({ ...f, collect_date: d ? format(d, "yyyy-MM-dd") : "" }));
                        setDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Heure</Label>
                <Select value={form.collect_time} onValueChange={(v) => setForm((f) => ({ ...f, collect_time: v }))}>
                  <SelectTrigger data-testid="public-collect-time" className="mt-1.5">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Clock className="w-4 h-4 text-brand-600" />
                      <SelectValue placeholder="Choisir un créneau" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {slots.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`public-time-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Button data-testid="public-submit" type="submit" disabled={loading || (!savedMode && !form.commune_id) || !isValidPhone(form.whatsapp)} className="w-full bg-brand-600 hover:bg-brand-700 h-12 text-base">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "SOUMETTRE MA COMMANDE"}
          </Button>
          </>)}

          {error && <p data-testid="public-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        </form>
      </div>
    </div>
  );
}
